import { Injectable } from '@angular/core';
import { AuthService } from '@app/auth/auth.service';
import { BehaviorSubject } from 'rxjs';
import { System } from 'src/app/models';
import { UserService } from 'src/app/shared/services';
import { SoundNotificationsService } from './sound-notifications.service';

export interface Notification {
  id: string;
  ref: string;
  uuid?: string; // ID único do item (ex: ticket.id, order.id, etc.)
  title: string;
  summary: string;
  dateTime: Date;
  icon?: string;
  iconColor?: string;
  iconTooltip?: string;
  userId: string;
  type: 'ticket' | 'order' | 'quote' | 'payment' | 'general'; // Tipos específicos de notificação
  metadata?: any; // Para dados específicos de cada tipo de notificação
}

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private readonly STORAGE_KEY = 'notifications';
  private readonly MAX_NOTIFICATIONS = 25;
  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();

  constructor(
    private soundNotificationsService: SoundNotificationsService,
    private authService: AuthService,
    private userService: UserService
  ) {
    this.initializeNotifications();
    this.setupStorageListener();
    this.setupTitleObserver();
  }

  private async initializeNotifications(): Promise<void> {
    await this.loadNotificationsFromStorage();
  }

  private async loadNotificationsFromStorage(): Promise<void> {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const allNotifications = JSON.parse(stored).map((n: any) => ({
          ...n,
          dateTime: new Date(n.dateTime),
        }));

        // Filtrar notificações do usuário logado
        const currentUser = await this.userService.getCurrentUser().catch(() => null);
        if (currentUser?.id) {
          const userNotifications = allNotifications.filter(
            (notification: Notification) => notification.userId === currentUser.id
          );
          this.notificationsSubject.next(userNotifications);
          this.updatePageTitle(userNotifications.length);
        } else {
          // Se não conseguir obter o usuário, não carregar notificações
          this.notificationsSubject.next([]);
          this.updatePageTitle(0);
        }
      } else {
        this.updatePageTitle(0);
      }
    } catch (error) {
      console.error('Erro ao carregar notificações do localStorage:', error);
      this.notificationsSubject.next([]);
      this.updatePageTitle(0);
    }
  }

  private setupStorageListener(): void {
    window.addEventListener('storage', event => {
      if (event.key === this.STORAGE_KEY) {
        this.initializeNotifications();
      }
    });
  }

  private setupTitleObserver(): void {
    // Observar mudanças no título da página para restaurar contador de notificações
    const titleObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'childList' && mutation.target === document.head) {
          const currentTitle = document.title;
          const notificationCount = this.notificationsSubject.value.length;

          if (notificationCount > 0) {
            const hasNotificationCount = /^\(\d+\)\s/.test(currentTitle);
            if (!hasNotificationCount) {
              setTimeout(() => {
                this.updatePageTitle(notificationCount);
              }, 100);
            }
          }
        }
      });
    });

    titleObserver.observe(document.head, {
      childList: true,
      subtree: true,
    });

    // Observar mudanças diretas no document.title
    let lastTitle = document.title;
    setInterval(() => {
      const currentTitle = document.title;
      if (currentTitle !== lastTitle) {
        lastTitle = currentTitle;
        const notificationCount = this.notificationsSubject.value.length;

        if (notificationCount > 0) {
          const hasNotificationCount = /^\(\d+\)\s/.test(currentTitle);
          if (!hasNotificationCount) {
            setTimeout(() => {
              this.updatePageTitle(notificationCount);
            }, 50);
          }
        }
      }
    }, 500);
  }

  async addNotification(notification: Omit<Notification, 'id' | 'dateTime'>): Promise<void> {
    const newNotification: Notification = {
      ...notification,
      id: this.generateId(),
      dateTime: new Date(),
    };

    // Carregar TODAS as notificações do localStorage (de todos os usuários)
    const allStoredNotifications = await this.loadAllNotificationsFromStorage();
    const currentUserNotifications = this.notificationsSubject.value;

    // Adicionar a nova notificação às notificações do usuário atual
    let updatedUserNotifications = [newNotification, ...currentUserNotifications];

    // Aplicar regra de limite: se exceder MAX_NOTIFICATIONS, remover as mais antigas
    if (updatedUserNotifications.length > this.MAX_NOTIFICATIONS) {
      updatedUserNotifications = updatedUserNotifications
        .sort((a, b) => b.dateTime.getTime() - a.dateTime.getTime())
        .slice(0, this.MAX_NOTIFICATIONS);
    }

    // Atualizar a lista completa com as notificações do usuário atual
    const updatedAllNotifications = this.updateAllNotificationsWithUserNotifications(
      allStoredNotifications,
      updatedUserNotifications,
      newNotification.userId
    );

    this.saveAllNotificationsToStorage(updatedAllNotifications);
    this.notificationsSubject.next(updatedUserNotifications);
    this.updatePageTitle(updatedUserNotifications.length);

    // Tocar som de notificação baseado no ícone (passando ref para verificar se está silenciado)
    this.soundNotificationsService.playNotificationSound(newNotification.icon, newNotification.ref);
  }

  async removeNotification(notificationId: string): Promise<void> {
    const currentNotifications = this.notificationsSubject.value;
    const updatedUserNotifications = currentNotifications.filter(n => n.id !== notificationId);

    // Carregar TODAS as notificações do localStorage
    const allStoredNotifications = await this.loadAllNotificationsFromStorage();

    // Encontrar o userId da notificação que está sendo removida
    const notificationToRemove = allStoredNotifications.find(n => n.id === notificationId);
    const userId = notificationToRemove?.userId;

    if (userId) {
      // Atualizar a lista completa removendo a notificação específica
      const updatedAllNotifications = allStoredNotifications.filter(n => n.id !== notificationId);
      this.saveAllNotificationsToStorage(updatedAllNotifications);
    }

    this.notificationsSubject.next(updatedUserNotifications);
    this.updatePageTitle(updatedUserNotifications.length);
  }

  deleteNotificationById(notificationId: string): void {
    this.removeNotification(notificationId);
  }

  /** Troca o `ref` de notificações já gravadas (ex.: chamado que ganhou número) e persiste. */
  async updateRefs(changes: { id: string; ref: string }[]): Promise<void> {
    if (!changes.length) return;
    const byId = new Map(changes.map(change => [change.id, change.ref]));
    const apply = (list: Notification[]) =>
      list.map(n => (byId.has(n.id) ? { ...n, ref: byId.get(n.id)! } : n));

    this.saveAllNotificationsToStorage(apply(await this.loadAllNotificationsFromStorage()));
    this.notificationsSubject.next(apply(this.notificationsSubject.value));
  }

  async clearAllNotifications(): Promise<void> {
    // Obter o usuário atual para limpar apenas suas notificações
    const currentUser = await this.userService.getCurrentUser().catch(() => null);
    if (currentUser?.id) {
      // Carregar TODAS as notificações do localStorage
      const allStoredNotifications = await this.loadAllNotificationsFromStorage();

      // Remover apenas as notificações do usuário atual
      const otherUsersNotifications = allStoredNotifications.filter(
        n => n.userId !== currentUser.id
      );

      this.saveAllNotificationsToStorage(otherUsersNotifications);
    }

    this.notificationsSubject.next([]);
    this.updatePageTitle(0);
  }

  getNotifications(): Notification[] {
    return this.notificationsSubject.value;
  }

  private async loadAllNotificationsFromStorage(): Promise<Notification[]> {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored).map((n: any) => ({
          ...n,
          dateTime: new Date(n.dateTime),
        }));
      }
      return [];
    } catch (error) {
      console.error('Erro ao carregar todas as notificações do localStorage:', error);
      return [];
    }
  }

  private updateAllNotificationsWithUserNotifications(
    allNotifications: Notification[],
    userNotifications: Notification[],
    userId: string
  ): Notification[] {
    // Remover todas as notificações do usuário atual da lista completa
    const otherUsersNotifications = allNotifications.filter(n => n.userId !== userId);

    // Adicionar as notificações atualizadas do usuário atual
    return [...otherUsersNotifications, ...userNotifications];
  }

  private saveAllNotificationsToStorage(notifications: Notification[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(notifications));
    } catch (error) {
      console.error('Erro ao salvar todas as notificações no localStorage:', error);
    }
  }

  private saveNotificationsToStorage(notifications: Notification[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(notifications));
    } catch (error) {
      console.error('Erro ao salvar notificações no localStorage:', error);
    }
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  public updatePageTitle(notificationCount: number): void {
    if (!this.authService.checkSystem([System.TICKET_CENTER])) {
      return;
    } else {
      const originalTitle = document.title;

      if (notificationCount > 0) {
        const titleMatch = originalTitle.match(/^\((\d+)\)\s*(.+)$/);

        if (titleMatch) {
          document.title = `(${notificationCount}) ${titleMatch[2]}`;
        } else {
          document.title = `(${notificationCount}) ${originalTitle}`;
        }
      } else {
        const titleMatch = originalTitle.match(/^\(\d+\)\s*(.+)$/);
        if (titleMatch) {
          document.title = titleMatch[1];
        }
      }
    }
  }
}
